/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";

import {
  colors,
  numericPercentages,
  numericPixels,
  numericValues,
  spacing,
} from "../../../../global/emotion";
import { stylesScreenReader } from "../../../../global/emotion/styles/stylesScreenReader";
import { AccordionContent } from "../../_base/_core/Accordion/AccordionContent.emotion";
import { AccordionItem } from "../../_base/_core/Accordion/AccordionItem.emotion";
import { AccordionTrigger } from "../../_base/_core/Accordion/AccordionTrigger.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { CheckboxIcon } from "../../_base/icons/Icon/GeneralIcons";

const accordionRowStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  height: numericPixels[64],
  justifyContent: "space-between",
  width: numericPercentages[100],
};
const checkboxContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flex: 1,
  flexDirection: "row",
  justifyContent: "flex-start",
  padding: "15px 30px 15px 3px",
};
const descriptionStyle: Interpolation = {
  padding: spacing.ml,
};
const disabledStyle: Interpolation = {
  opacity: numericValues["0.5"],
};
const labelStyle: Interpolation = {
  color: colors.purple60,
  flexShrink: 1,
  paddingLeft: spacing.ms,
  textWrap: "nowrap",
};

type ConsentCheckboxProps = {
  description: string;
  disabled?: boolean;
  id: string;
  isChecked: boolean;
  onCheck: (isChecked: boolean, value: number) => void;
  label: string;
  value: number;
};

export const ConsentCheckbox = ({
  description,
  disabled = false,
  id,
  isChecked,
  onCheck,
  label,
  value,
}: ConsentCheckboxProps) => {
  const toggleCheck = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!disabled) onCheck(!isChecked, value);
  };

  return (
    <AccordionItem value={id}>
      <AccordionTrigger css={accordionRowStyle}>
        <div
          css={[checkboxContainerStyle, disabled && disabledStyle]}
          onClick={(e) => toggleCheck(e)}
          // clicking this element checks the <input>, it does not trigger the AccordionItem
          data-disabled
        >
          <input
            css={stylesScreenReader}
            type="checkbox"
            id={id}
            checked={isChecked}
            readOnly
          />
          {isChecked ? (
            <CheckboxIcon.Checked fill={colors.purple60} size={40} />
          ) : (
            <CheckboxIcon.Unchecked fill={colors.purple60} size={40} />
          )}
          <Text asChild size="m" type="button">
            <label css={labelStyle} htmlFor={id}>
              {label}
            </label>
          </Text>
        </div>
      </AccordionTrigger>
      <AccordionContent css={descriptionStyle}>{description}</AccordionContent>
    </AccordionItem>
  );
};
